import { useState } from 'react'
import { PageMeta } from '@/components/seo/PageMeta'
import { Container, Heading, Text, Card, Flex, Button, TextArea, TextField, Select, Callout } from '@radix-ui/themes'
import { MessageSquare, Send, CheckCircle, Loader2 } from 'lucide-react'

export default function FeedbackPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [formData, setFormData] = useState({
    type: 'feedback',
    subject: '',
    message: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500))
    setIsLoading(false)
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <>
        <PageMeta
          title="Feedback Sent"
          description="Thank you for your feedback"
        />
        <Container size="2" className="py-16">
          <Card>
            <Flex direction="column" align="center" gap="4" py="8">
              <CheckCircle className="h-16 w-16 text-green-500" />
              <Heading size="5">Thank You!</Heading>
              <Text color="gray" align="center">
                Your feedback has been submitted successfully. We appreciate your input
                and will review it carefully.
              </Text>
              <Button variant="soft" onClick={() => {
                setSubmitted(false)
                setFormData({ type: 'feedback', subject: '', message: '' })
              }}>
                Submit More Feedback
              </Button>
            </Flex>
          </Card>
        </Container>
      </>
    )
  }

  return (
    <>
      <PageMeta
        title="Feedback"
        description="Share your feedback with us"
      />

      <Container size="2" className="py-8">
        <Flex direction="column" gap="6">
          {/* Header */}
          <Flex direction="column" gap="1" align="center" className="text-center">
            <MessageSquare className="h-12 w-12 text-blue-500 mb-2" />
            <Heading size="7">Send Feedback</Heading>
            <Text color="gray" size="2">
              We'd love to hear from you! Share your thoughts, suggestions, or report issues.
            </Text>
          </Flex>

          {/* Feedback Form */}
          <Card>
            <form onSubmit={handleSubmit}>
              <Flex direction="column" gap="4">
                <Flex direction="column" gap="2">
                  <Text size="2" weight="medium">Feedback Type</Text>
                  <Select.Root
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                  >
                    <Select.Trigger placeholder="Select type" />
                    <Select.Content>
                      <Select.Item value="feedback">General Feedback</Select.Item>
                      <Select.Item value="bug">Bug Report</Select.Item>
                      <Select.Item value="feature">Feature Request</Select.Item>
                      <Select.Item value="question">Question</Select.Item>
                    </Select.Content>
                  </Select.Root>
                </Flex>

                <Flex direction="column" gap="2">
                  <Text size="2" weight="medium">Subject</Text>
                  <TextField.Root
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Brief summary of your feedback"
                    required
                  />
                </Flex>

                <Flex direction="column" gap="2">
                  <Text size="2" weight="medium">Message</Text>
                  <TextArea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Describe your feedback in detail..."
                    rows={6}
                    required
                  />
                </Flex>

                <Callout.Root color="blue" variant="soft">
                  <Callout.Text>
                    Your feedback helps us improve Templatean. All submissions are reviewed by our team.
                  </Callout.Text>
                </Callout.Root>

                <Button type="submit" size="3" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Submit Feedback
                    </>
                  )}
                </Button>
              </Flex>
            </form>
          </Card>

          {/* Additional Info */}
          <Flex direction="column" gap="2" className="text-center">
            <Text size="2" color="gray">
              Need immediate help?
            </Text>
            <Text size="2">
              Check our <a href="/help" className="text-blue-500 hover:underline">Help Center</a> or{' '}
              <a href="/help/contact" className="text-blue-500 hover:underline">contact support</a>.
            </Text>
          </Flex>
        </Flex>
      </Container>
    </>
  )
}
